import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Check, Loader2, AlertCircle, ArrowRight } from "lucide-react";
import SiteShell from "../components/site/SiteShell";
import { api } from "../lib/api";

export default function PaySuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ loading: true, paid: false, error: null, data: null });
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus({ loading: false, paid: false, error: "Missing session_id", data: null });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      tries.current += 1;
      try {
        const { data } = await api.get(`/payment-status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setStatus({ loading: false, paid: true, error: null, data });
          return;
        }
        if (data.status === "expired" || tries.current >= 8) {
          setStatus({ loading: false, paid: false, error: data.status === "expired" ? "Session expired" : "Still processing — check email for confirmation.", data });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (cancelled) return;
        if (tries.current < 6) setTimeout(poll, 2000);
        else setStatus({ loading: false, paid: false, error: "Could not verify payment", data: null });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <SiteShell>
      <section className="min-h-[70vh] flex items-center py-20" data-testid="pay-success-section">
        <div className="max-w-[640px] w-full mx-auto px-6 text-center">
          {status.loading && (
            <>
              <Loader2 className="mx-auto text-[#d4af37] animate-spin mb-6" size={48} strokeWidth={1.4} />
              <h1 className="h-display text-white text-[36px] mb-4">CONFIRMING <span className="text-[#d4af37]">PAYMENT.</span></h1>
              <p className="text-white/65">Stripe is finalising your transaction — usually under 10 seconds.</p>
            </>
          )}
          {!status.loading && status.paid && (
            <>
              <Check className="mx-auto text-[#d4af37] mb-6" size={48} strokeWidth={1.4} />
              <h1 className="h-display text-white text-[40px] sm:text-[56px] mb-4">PAYMENT <span className="text-[#d4af37]">RECEIVED.</span></h1>
              <p className="text-white/70 mb-8">
                Thank you. Your campaign is now in production and our data team will be in touch within one business day.
              </p>
              {status.data && (
                <div className="bg-[#111111] border border-[#d4af37]/25 p-6 text-left mb-8 inline-block">
                  <div className="kicker text-[#d4af37] mb-2">Receipt</div>
                  <div className="text-white/85 text-[14px]">${(status.data.amount_total / 100).toFixed(2)} {status.data.currency?.toUpperCase()}</div>
                  <div className="text-white/40 text-[11px] mt-1 tracking-wider">Session: {sessionId.slice(0, 18)}…</div>
                </div>
              )}
              <div className="block">
                <Link to="/" className="btn-outline-gold btn-label inline-flex" data-testid="success-home-link">
                  Back to Home <ArrowRight size={14} />
                </Link>
              </div>
            </>
          )}
          {!status.loading && !status.paid && (
            <>
              <AlertCircle className="mx-auto text-amber-400 mb-6" size={48} strokeWidth={1.4} />
              <h1 className="h-display text-white text-[36px] mb-4">PAYMENT <span className="text-amber-400">PENDING.</span></h1>
              <p className="text-white/70 mb-8">{status.error || "We couldn't confirm payment automatically. You'll receive a confirmation email shortly."}</p>
              <Link to="/" className="btn-outline-gold btn-label inline-flex">Back to Home <ArrowRight size={14} /></Link>
            </>
          )}
        </div>
      </section>
    </SiteShell>
  );
}
